// ABOUTME: Owns point-tag match-state (SPEC-26 D2/D3/AC-3a) — runs the generalized matcher against
// ABOUTME: the HEARD portion of agent speech, resolves content, and renders 0-N PointTags at anchors.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { matchAttributes } from '../attributeMatcher';
import { resolveTagContent } from './resolveTagContent';
import { staticTagsForChapter } from './staticChapterTags';
import { suppressRedundantTags } from './suppressRedundantTags';
import {
  nativeFrameSize, hasAnchorsForBreakpoint, orderedKeysForChapter,
} from './chapterTagAnchors';
import { resolveStickyAnchors, stickyScreenTops } from './tagPlacement';
import { useBreakpoint, useVideoBox, useViewportSize, useDeviceProfile } from '../useViewportSize';
import { Reveal } from '../../../animation/revealTransition';
import { mapNativePoint, clampPercent } from '../videoBoxMath';
import { PlayerState } from '../useVideoPlayer';
import { useTranscriptBySender } from '../../../state/transcriptStore';
import { useVoicePacedReveal } from './captionReveal';
import PointTag, { authoredPlateHeight } from './PointTag';
import { PRODUCT_CARD_CHAPTERS, PRODUCT_CARD_MOBILE, PRODUCT_CARD_MOBILE_NAME_MAX_H } from './ReelProductCard';
import { BAR_BOTTOM_PCT, BAR_H_PCT } from './CaptionOverlay';
import { useControlsLift, useLiftTransition } from '../../../uiExperiment/bottomChromeStore'; // SPEC-82 §4.7
import { desktopLiftPct } from '../../../uiExperiment/controlsLift'; // SPEC-82 OQ-3
import {
  MOBILE_UNIT, mu, safeBottom, TAG_ROW, TAG_ROW_BOTTOM, mobileUnitFor,
} from '../../ReelChrome/mobileGeometry';

// Nudges a tag whose anchor is on screen but so close to an edge that the box would hang off it.
// Anchors the crop has removed altogether are dropped before this, not clamped — see below.
const SAFE_BAND_MIN_PCT = 2;
// The horizontal (left/right) edge band. Unrelated to the bottom-chrome concern below — kept as its
// own constant so tightening the vertical ceiling can never silently reclamp a tag's LEFT position.
const SAFE_BAND_MAX_LEFT_PCT = 88;
// The desktop CC bar (ReelFooter/CaptionOverlay.Bar) is fixed chrome pinned to the VIEWPORT, not a
// point on the cropped video — its top edge is a constant percentage of screen height regardless of
// how the object-fit:cover crop maps a native-frame anchor. On a wider-than-16:9 viewport the
// vertical crop pushes a lower-half anchor's mapped position down until it hits this ceiling —
// movement's `crystal` tag was the one reported overlapping the bar (2026-08-20). The ceiling is
// derived from the bar's real geometry plus the tallest authored desktop plate so a maximally
// clamped tag's box clears the bar with room to spare, and self-corrects if either geometry changes.
const FOOTER_TOP_PCT = 100 - (BAR_BOTTOM_PCT + BAR_H_PCT);
// How many lines of room to keep for a tag. The box hugs its copy up to MAX_TAG_WIDTH_PCT and only
// then wraps, so one line is the common case and two is the realistic worst — and the gutter below
// absorbs a third (a 3-line plate is 11.12% of the authored frame's height against 8.71% + 3%
// reserved here).
const TAG_WRAP_LINES = 2;
const FOOTER_GUTTER_PCT = 3; // visual breathing room above the bar, on top of the tag's own height

/**
 * The lowest a tag's TOP may be clamped to, as a percentage of the viewport, so its bottom still
 * clears the CC bar.
 *
 * Taken from what PointTag actually renders. It used to be `max(anchor.h)` over the authored table,
 * and that stopped being a stand-in for the tag's height on 2026-09-04: the anchors were sized for
 * the old two-part plate (199-315 native px) while a single-line tag comes out at 136. The tallest
 * anchor is 315, i.e. 14.58% of frame height reserved for a plate occupying 6.3% — which pinned a
 * low anchor ~72px higher up the screen than it needed to be. Most visible on `exploded` at
 * 3440x1440, where the bottom callout was clamped clear of the dial it points at with a wide dead
 * band under it.
 *
 * ── Why it is per-render and not a constant ─────────────────────────────────
 * The bar is VIEWPORT chrome (a fixed percentage of screen height) and the plate is PICTURE-scaled
 * (native px times the cover-crop scale), so the two are only in the same units at 16:9. Dividing
 * the plate by the native frame height, as this did until 2026-09-04, understates it on any wider
 * viewport — at 3440x1440 the real figure is 11.70% against the 8.71% reserved, which eats the
 * whole gutter, and at 5120x1440 a clamped tag overhangs the bar by ~82px. Reachable since the
 * re-author put authored anchors at y=1517 (movement) and y=1568 (crown), both of which really do
 * clamp there. At 16:9 the two expressions agree exactly, so the laptop render is untouched.
 */
function maxTopPctFor(videoBox) {
  const plateHeightPct = videoBox?.containerH
    ? (authoredPlateHeight('desktop', TAG_WRAP_LINES) * videoBox.scale / videoBox.containerH) * 100
    : (authoredPlateHeight('desktop', TAG_WRAP_LINES) / nativeFrameSize('desktop').height) * 100;
  // Never let the ceiling fall through the floor: `clampPercent` is min-then-max, so an inverted
  // pair would return the MAX and park the tag off the top of the screen. Only reachable on an
  // absurd aspect ratio, but the failure mode is silent and total.
  return Math.max(FOOTER_TOP_PCT - plateHeightPct - FOOTER_GUTTER_PCT, SAFE_BAND_MIN_PCT);
}
// A tag squeezed narrower than this stops being readable — better to let it overhang slightly.
const MIN_TAG_WIDTH_PCT = 12;
// Content-driven sizing (2026-08-27): the box hugs its text (PointTag sets width: max-content) and
// this is the WRAP ceiling, not a fixed width. The authored anchor widths were sized to the Figma
// copy, so clamping to them wrapped any longer resolved value mid-phrase; the widest authored
// desktop plate (crown's Case_Material, 832/3840 ≈ 22%) fits comfortably under this ceiling.
const MAX_TAG_WIDTH_PCT = 30;

// The SPEECH-DRIVEN ceiling only (see `visibleCeiling`): a static chapter is capped by its own
// authored tag count instead, because the frame has already decided how busy the picture gets.
//
// Client decision 2026-08-17, amended 2026-08-18: three is still the most the frame carries at
// once, but it is a concurrency CEILING now, not a persistence cap. A tag fades in, dwells about
// four seconds, and fades away on its own — an expansive answer streams its attributes through the
// frame a few at a time instead of parking the first three over the film for the whole hold. A
// fourth match arriving while three are live fades the oldest out early rather than snapping it
// away; a key that has had its moment can be earned again by a fresh mention in a later sentence.
const MAX_VISIBLE_TAGS = 3;
// Exported (SPEC-53) so the collection stage's labels fade on the reel tag's own clock.
export const TAG_FADE_MS = 400; // must match PointTag's reveal duration (animation REVEAL_MS)
// Client, 2026-09-03: on DESKTOP a tag no longer fades — it tweens open from its top-left corner
// (the anchor point itself) and collapses back into it. Mobile keeps the fade: those tags sit in a
// flow row of chrome rather than at a point on the watch, so there is no corner for the gesture to
// come out of, and three plates scaling in a row reads as the row itself jittering. The fade path
// is not dead code — it is still every other overlay's treatment, and the reduced-motion fallback.
const DESKTOP_TAG_REVEAL = Reveal.SCALE_CORNER;
const MOBILE_TAG_REVEAL = Reveal.FADE;
// Removal runs a beat AFTER the fade nominally completes: unmounting at exactly TAG_FADE_MS can
// cut the transition's last frames under load, popping the plate off instead of easing it out.
const TAG_REMOVE_SLACK_MS = 80;
// A slot a tag fully faded out at is SPENT for the rest of the hold: the next key cascades to a
// fresh slot instead of fading in at the exact pixel an earlier plate vacated — back-to-back blur
// plates at one position read as a single tag flickering (reported 2026-08-27; hardened same day
// from a 900ms cooldown to per-hold, per the client's "never at the same position"). The cascade
// falls back to reusing spent slots only once every fresh one is exhausted (tagPlacement.js).
// Doubled from the original 2000ms (2026-08-20) — tags were fading before a guest had time to read
// them, especially the longer description/value copy.
const TAG_VISIBLE_MS = 4000;    // dwell at full opacity before fading away
const TAG_ENTER_DELAY_MS = 30;  // mount at 0, flip to 1 a beat later so the fade-in actually runs
// When a settle reveals several buffered matches at once, their fade-ins are spaced out so they
// cascade into the frame instead of arriving as one block. Admission itself is NOT staggered — the
// concurrency ceiling and positions are decided immediately, only the opacity flips are paced.
export const TAG_STAGGER_MS = 320; // exported for the collection stage (SPEC-53), see TAG_FADE_MS
// A finished turn's text stays matchable this long after its final message lands. Without some
// window a text-only turn (no audio clock) could never match after settling; without a CUTOFF a
// hold reached minutes later would resurrect tags for speech nobody remembers.
const RECENT_FINAL_MS = 10_000;

/**
 * Where the mobile tag row's underside sits, in authored mobile px.
 *
 * Normally `TAG_ROW_BOTTOM` — one gutter above the CC bar, which is where every mobile frame draws
 * the row. `watch_360` is the exception: it is the one chapter that also shows a product card, and
 * that card is bottom-anchored to the SAME band (313 against the row's 311, so the two compositions
 * cannot drift apart on an odd-shaped viewport). Until 2026-09-04 that was harmless, because
 * watch_360 authored no tags at all; now it authors three, and stacking them into the card's own
 * 207px box would print one over the other.
 *
 * The mobile frame (45:2834) does NOT answer this — it draws the card and no tag row, and the three
 * tags are on mobile by client instruction rather than by design. So the row is lifted a full card
 * plus one gutter, which puts it exactly where a second row of bottom chrome belongs.
 *
 * `hasCard` is the caller's answer to "will the card actually be in this frame" — it needs a
 * product envelope to render at all, and lifting the row over a card that never appears would
 * leave the tags floating in the middle of the picture with a hole beneath them. It is
 * deliberately NOT the card's full at-rest gate: that goes false the moment a departure is queued,
 * which would drop the row a whole card's height while both surfaces are still fading out.
 *
 * Exported for its test: the rendered value is a `calc()` carrying a `var()`, which jsdom drops
 * from the CSSOM outright, so the style attribute is the one place this CANNOT be asserted.
 */
export function mobileRowBottom(chapterId, hasCard = true) {
  if (!hasCard || !PRODUCT_CARD_CHAPTERS.includes(chapterId)) return TAG_ROW_BOTTOM;
  return TAG_ROW_BOTTOM + renderedCardHeight() + TAG_ROW.gutterAboveBar;
}

/**
 * How tall the mobile product card actually RENDERS, in authored mobile px.
 *
 * NOT its authored `h` of 207. That box is cap-trimmed the way every Figma text frame in this
 * design is, and mobile deliberately does not compensate for the trim (see PointTag's COMPOSITION
 * note — the authored 157 tag box decomposes as neither padding + caps nor padding + line boxes,
 * so there is no trim to correct and compensating would be inventing a number). The card therefore
 * comes out at padding + two real line boxes, ~15% over the drawn figure; lifting the row by the
 * authored box left the two touching (seen on the `mobile-tags-watch_360` plate).
 *
 * Uses the card's own line-clamp ceiling (`PRODUCT_CARD_MOBILE_NAME_MAX_H`), not a hardcoded "2
 * lines": a catalogue title, or a waitlist variant's title+subtitle combined, can run longer than
 * 2 lines, and ReelProductCard.js now clips its name column at that SAME ceiling rather than
 * growing past it — before that clip existed, a long enough name grew the card past this
 * function's old 2-line assumption and painted straight over the tag row it was supposed to clear
 * (reported as "a 4th tag UI that shouldn't be there", 2026-09-15). The two have to move together,
 * which is why this reads the card's own constant instead of a second copy of "3".
 *
 * Derived here rather than in ReelProductCard so this change stays inside the tag layer, and from
 * the card's own exported tokens rather than a literal so a retune cannot put the row back in it.
 */
function renderedCardHeight() {
  const card = PRODUCT_CARD_MOBILE;
  return card.padY * 2 + PRODUCT_CARD_MOBILE_NAME_MAX_H;
}

/**
 * SPEC-26 Phase 4b — the mobile composition's tag surface.
 *
 * Not a positioned layer at all: the mobile frames put every tag in one bottom-anchored row above
 * the CC bar (45:3297 groups movement's three that way explicitly), so this is flow layout and none
 * of the crop-aware anchor maths above applies. That is the point of the split — a tag pinned to a
 * spot on the watch has to track the object-fit:cover crop; a row of chrome tracks the viewport.
 *
 * The row does not clip an over-long value the way the desktop tag does. Desktop clips because its
 * neighbours sit 51 authored px away and a grown box would collide with them; here the row's only
 * neighbour is the bar below it and the box grows upward into empty frame, so clipping brand copy
 * would cost content for no layout gain.
 *
 * `justify-between`, not `justify-center` (owner report 2026-09-15): movement's tags are the only
 * ones whose authored widths fill the row exactly (mobileGeometry.js's TAG_ROW comment) — every
 * shorter chapter's three centred pills sat inset from the row's own edges by however much shorter
 * their combined content was, so the group's outer edges drifted chapter to chapter instead of
 * lining up with the CC bar/mic column beneath them (37 authored inset both sides — the row's own
 * 40 is within a few px of it). Spacing the three pills to the row's own padded edges instead pins
 * the group's outer edges to that same span on every chapter, independent of content length.
 */
function MobileTagRow({ tags, scale, reducedMotion, bottom = TAG_ROW_BOTTOM }) {
  // SPEC-82 control_reveal: the row rises with the revealed controls (0 / no transition when the toggle is off).
  const lift = useControlsLift('mobile');
  const liftTransition = useLiftTransition('bottom');
  return (
    <div
      data-testid="reel-tag-row"
      className="absolute left-0 right-0 flex items-end justify-between pointer-events-none"
      style={{
        '--u': MOBILE_UNIT,
        bottom: safeBottom(bottom + lift),
        ...(liftTransition ? { transition: liftTransition } : {}),
        gap: mu(TAG_ROW.gap),
        paddingLeft: mu(TAG_ROW.sideInset),
        paddingRight: mu(TAG_ROW.sideInset),
      }}
    >
      {tags.map(({ key, phase, content }) => (
        <PointTag
          key={key}
          label={content.label}
          value={content.value}
          lines={content.lines}
          reserve={content.reserve}
          composition="mobile"
          positioned={false}
          scale={scale}
          opacity={phase === 'live' ? 1 : 0}
          reveal={MOBILE_TAG_REVEAL}
          reducedMotion={reducedMotion}
        />
      ))}
    </div>
  );
}

/**
 * @param {{
 *   product: object|null,           // reel_product envelope (D5)
 *   tagEligibility: object|null,    // D3 map — chapters a matched key may surface in
 *   chapterId: string|null,         // active reel chapter (overlayInfo.chapterId)
 *   playerState: string,            // useVideoPlayer's PlayerState
 *   pendingChapterId: string|null,  // D2 — a departure is queued when non-null
 * }} props
 */
function PointTagLayer({
  product, tagEligibility, tagRegions, chapterId, playerState, pendingChapterId, offSubject,
}) {
  const agentMessage = useTranscriptBySender('agent');
  // THE HEARD PREFIX. Gemini's transcript arrives far faster than the voice that speaks it — the
  // same asymmetry the CC bar already corrects with the audio playback clock (captionReveal /
  // speechClock). Matching against the RAW transcript made tags surface for specs the guest had
  // not heard yet, dwell out their 4s, and be spent by the time the voice actually reached them
  // (reported 2026-08-25 as "not logically shown"). Matching against the paced reveal keeps every
  // tag in step with the words as they are actually spoken — and it is strictly CHEAPER than the
  // old path, because the matcher now runs on ~80ms reveal ticks instead of every 10ms partial.
  // With no audio clock at all (text-only turn, tests) the reveal is a pass-through.
  // NOTE: the CC bar keeps its own reveal cursor over the same global speechClock. Both are
  // monotone and seeded identically, so they converge; a mid-turn mount can briefly disagree by a
  // word, which matters to neither surface.
  const heardText = useVoicePacedReveal(agentMessage?.text);
  const heardTextRef = useRef(heardText);
  heardTextRef.current = heardText;
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  // STATIC chapters (staticChapterTags.js — every tagged chapter as of 2026-09-04): the design's
  // authored callouts render verbatim for the whole hold and the speech-matching engine below is
  // bypassed entirely. Resolved per BREAKPOINT because the two compositions do not always carry
  // the same number of tags (movement authors five plates and the mobile row holds three).
  const staticTags = staticTagsForChapter(chapterId, breakpoint);
  // How many tags may be live at once. A static chapter's ceiling is its own authored count: the
  // frame is the design's own decision about how busy the picture may get, and MAX_VISIBLE_TAGS is
  // a rule for the speech-driven trickle, where tags arrive one at a time and the oldest has to
  // make way. Capping movement at three would simply never draw two of its five plates.
  const visibleCeiling = staticTags ? staticTags.length : MAX_VISIBLE_TAGS;
  const frame = nativeFrameSize(breakpoint);
  const videoBox = useVideoBox(frame.width, frame.height);
  const { width: viewportWidth, height: viewportHeight } = useViewportSize();
  const desktopLift = useControlsLift('desktop'); // SPEC-82 control_reveal: 0 unless the row is open
  const { reducedMotion } = useDeviceProfile();
  // Dwell lifecycle: every visible tag is an entry { key, phase } — 'in' (mounted at opacity 0),
  // 'live' (faded in, dwelling), 'out' (fading away, leaves the DOM one fade later).
  const [entries, setEntries] = useState([]);
  const entriesRef = useRef([]);          // mirror for the timer callbacks — state is per-render
  const timersRef = useRef(new Map());    // key -> Set<timeout id> (enter flip, dwell, removal)
  // Keys that already had their moment this utterance — earned at ADMISSION, released only by a
  // genuinely new sentence. This is what stops a still-growing streamed sentence re-flickering the
  // same tag, and what stops a settle re-showing a tag the previous hold already displayed.
  // Deliberately NOT cleared on a chapter change: a key eligible in two chapters that already had
  // its moment does not repeat itself in the destination mid-turn.
  const earnedRef = useRef(new Set());
  const utteranceRef = useRef(null);      // last full speech text seen; a non-extension = new turn
  // How many characters of the CURRENT utterance can no longer match. Set to the HEARD length
  // when a departure is queued (or a chapter hard-cuts): everything the guest has already heard
  // belongs to the old picture and can never resurface as a tag in the destination — while
  // everything the voice says DURING and AFTER the move stays matchable and is revealed together
  // once the new hold settles. Measured against `heardText`, NOT the raw transcript: the raw text
  // is typically complete within the first second of an answer, so consuming its full length
  // would swallow the destination narration too and reveal nothing at the new hold.
  const consumedLenRef = useRef(0);
  // How far into the utterance the matcher has already looked (absolute chars). What separates a
  // TRICKLE (a key matched by words the guest just heard — may evict the oldest live tag, per the
  // 2026-08-18 client decision) from a CARRIED-OVER match (a key deferred from an earlier run
  // because the frame was full — waits for a slot instead of evicting).
  const scannedLenRef = useRef(0);
  const chapterRef = useRef(chapterId);
  const wasPendingRef = useRef(Boolean(pendingChapterId));
  // Sticky placement (tagPlacement.js) — a tag never moves while mounted. Two layers of it:
  // native-space anchors, and the post-clamp screen tops (each with its own invalidation sig).
  const placementCacheRef = useRef(new Map());
  const placementSigRef = useRef(null);
  const coolingSlotsRef = useRef([]); // {key, anchor} — spent slots this hold (see note above)
  const screenTopCacheRef = useRef(new Map());
  const screenTopSigRef = useRef(null);
  // Mirrored on every render (not read in an effect) so the transition effect below doesn't need
  // `agentMessage` as a dep — it must fire only on chapter/pending changes.
  const latestAgentMessageRef = useRef(agentMessage);
  latestAgentMessageRef.current = agentMessage;

  const setEntriesBoth = useCallback((next) => {
    entriesRef.current = next;
    setEntries(next);
  }, []);

  const addTimer = useCallback((key, id) => {
    if (!timersRef.current.has(key)) timersRef.current.set(key, new Set());
    timersRef.current.get(key).add(id);
  }, []);

  const clearTimersFor = useCallback((key) => {
    const ids = timersRef.current.get(key);
    if (ids) {
      ids.forEach(clearTimeout);
      timersRef.current.delete(key);
    }
  }, []);

  // Instant removal — reserved for a HARD picture change (arrival cut / instant chapter swap),
  // where a tag lingering over the wrong picture is worse than the pop.
  const clearAllTags = useCallback(() => {
    timersRef.current.forEach((ids) => ids.forEach(clearTimeout));
    timersRef.current.clear();
    setEntriesBoth([]);
  }, [setEntriesBoth]);

  // Fade a tag away and free its slot once the fade completes.
  const beginExit = useCallback((key) => {
    const entry = entriesRef.current.find((e) => e.key === key);
    if (!entry || entry.phase === 'out') return;
    clearTimersFor(key); // the pending dwell timer, when this is an early (over-cap) exit
    setEntriesBoth(entriesRef.current.map((e) => (e.key === key ? { ...e, phase: 'out' } : e)));
    addTimer(key, setTimeout(() => {
      timersRef.current.delete(key);
      const anchor = placementCacheRef.current.get(key);
      if (anchor) {
        coolingSlotsRef.current = [
          ...coolingSlotsRef.current.filter((c) => c.key !== key), { key, anchor },
        ];
      }
      setEntriesBoth(entriesRef.current.filter((e) => e.key !== key));
    }, TAG_FADE_MS + TAG_REMOVE_SLACK_MS));
  }, [addTimer, clearTimersFor, setEntriesBoth]);

  // The graceful counterpart to clearAllTags: everything eases out over the first beats of
  // whatever ended the hold (a move starting, a departure queued, the voice going off-subject).
  // The old instant unmount here was a large part of the reported "messy appear/disappear".
  const fadeOutAll = useCallback(() => {
    entriesRef.current.forEach((e) => { if (e.phase !== 'out') beginExit(e.key); });
  }, [beginExit]);

  // `persist: true` (static chapters, staticChapterTags.js) skips the dwell timer: the tag stays
  // at full opacity until something ends the hold — there is no auto-fade for authored callouts.
  const admitTag = useCallback((key, burstIndex = 0, { persist = false } = {}) => {
    clearTimersFor(key); // a fresh mention mid-fade supersedes the exit — the tag is resurrected
    const next = [...entriesRef.current.filter((e) => e.key !== key), { key, phase: 'in' }];
    setEntriesBoth(next);
    const alive = next.filter((e) => e.phase !== 'out');
    if (alive.length > visibleCeiling) beginExit(alive[0].key);
    const flipAt = TAG_ENTER_DELAY_MS + burstIndex * TAG_STAGGER_MS;
    addTimer(key, setTimeout(() => {
      // Two animation frames between mount and the opacity flip guarantee the browser has painted
      // the opacity-0 state first — a bare timeout raced the paint under decode/blur load, so the
      // fade-in sometimes ran and sometimes popped (part of the reported jitter). The phase guard
      // makes a stale frame callback a no-op if the tag already began exiting meanwhile.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setEntriesBoth(entriesRef.current.map((e) => (
          e.key === key && e.phase === 'in' ? { ...e, phase: 'live' } : e
        )));
      }));
    }, flipAt));
    if (!persist) addTimer(key, setTimeout(() => beginExit(key), flipAt + TAG_VISIBLE_MS));
  }, [addTimer, beginExit, clearTimersFor, setEntriesBoth, visibleCeiling]);

  // Unmount: stop every pending dwell/fade timer; the entries die with the component.
  useEffect(() => () => {
    timersRef.current.forEach((ids) => ids.forEach(clearTimeout));
  }, []);

  // D2 — transitions. A queued departure CONSUMES everything said so far (the announcing sentence
  // must not tag the destination — the HIGH regression of 2026-08-14) and eases the tags out over
  // the start of the move. The arrival cut then hard-clears any straggler still mid-fade. An
  // instant chapter change (no defer phase) does both at once. Consumption happens ONCE per
  // transition: re-consuming on arrival would throw away exactly the speech the move buffered.
  useEffect(() => {
    const wasPending = wasPendingRef.current;
    wasPendingRef.current = Boolean(pendingChapterId);
    if (pendingChapterId && !wasPending) {
      consumedLenRef.current = heardTextRef.current?.length ?? 0;
      fadeOutAll();
    }
    if (chapterId !== chapterRef.current) {
      chapterRef.current = chapterId;
      if (!wasPending) {
        consumedLenRef.current = heardTextRef.current?.length ?? 0;
      }
      coolingSlotsRef.current = []; // spent slots belong to the previous hold's picture
      clearAllTags();
    }
  }, [chapterId, pendingChapterId, fadeOutAll, clearAllTags]);

  // `playerState` flips to HOLDING/FROZEN the instant the state machine decides the hold has
  // begun — the SAME render/paint cycle in which the video may still be settling onto its final
  // frame. Mounting several new backdrop-blurred tags in that exact frame was overlapping the tail
  // of the move/settle and reading as a stutter/freeze (reported 2026-08-17). `settled` only flips
  // true two animation frames after entering a hold, so tag-mount work never lands in the same
  // frame as the video's own transition.
  const isHold = playerState === PlayerState.HOLDING || playerState === PlayerState.FROZEN;
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!isHold) {
      setSettled(false);
      return;
    }
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSettled(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isHold]);

  // `offSubject` (SPEC-27 D18) is a SPEECH-DRIVEN concern and applies only to the speech-driven
  // path. There, a tag IS the answer — the voice has moved to something this chapter cannot show,
  // and a tag mounted for the previous subject would sit under an unrelated one. A static callout
  // is not an answer: it is a fixed label the frame draws on the picture, and it has no
  // relationship to what is being discussed. Fading the design's own labels out mid-conversation
  // and back in when the subject returns is a blink with no visible cause (2026-09-04).
  const atRest = !pendingChapterId && isHold && settled;
  const canShow = atRest && (Boolean(staticTags) || !offSubject);

  // Static admission (the table itself is resolved at the top of the component). Admission is
  // staggered like a dynamic burst, but `persist` skips the dwell timer — the tags fade only when
  // the hold ends (a queued departure eases them out, an instant chapter change hard-clears them,
  // both via the existing transition effect above).
  useEffect(() => {
    if (!staticTags || !canShow) return;
    staticTags.forEach(({ key }, i) => {
      const alive = entriesRef.current.some((e) => e.key === key && e.phase !== 'out');
      if (!alive) admitTag(key, i, { persist: true });
    });
  }, [staticTags, canShow, admitTag]);

  // Anything that ends the at-rest state eases the tags out rather than snapping them away. This
  // single seam covers a move starting, a dip, a departure and off-subject (SPEC-27 D18/AC-26) —
  // in the D18 case the voice has moved to something this chapter does not show, and tags mounted
  // for the previous subject would otherwise sit under an unrelated answer.
  useEffect(() => {
    if (!canShow) fadeOutAll();
  }, [canShow, fadeOutAll]);

  useEffect(() => {
    if (staticTags) return; // static chapters never admit from speech
    const msg = latestAgentMessageRef.current;
    if (!msg || !msg.text) return;
    // Utterance boundary: streaming partials only ever EXTEND the current text (and the final
    // amendment repeats it verbatim), so a text that is not an extension is a new turn — which
    // releases every earned key and voids the previous turn's consumed prefix.
    // (A NEW message carrying byte-identical text — e.g. the backend's final amendment — reads as
    // an extension by design; a genuinely repeated identical turn is indistinguishable from it
    // and will not re-tag. Known, accepted: the amendment case is frequent, the repeat case rare.)
    const prevUtterance = utteranceRef.current;
    if (prevUtterance == null || !msg.text.startsWith(prevUtterance)) {
      earnedRef.current = new Set();
      consumedLenRef.current = 0;
      scannedLenRef.current = 0;
    }
    utteranceRef.current = msg.text;
    // Not at rest — nothing is admitted, but nothing is lost either: the transcript retains the
    // whole turn, and this effect re-runs the moment `canShow` flips true, revealing everything
    // heard-but-unshown (minus the consumed prefix) in one staggered burst.
    if (!canShow) return;
    // A dead turn's text must not resurrect tags on a much later hold. "Live" means: still
    // streaming, still being spoken (the paced reveal is behind the full text), or finished
    // within the last few seconds.
    const turnIsLive = msg.isPartial
      || heardText.length < msg.text.length
      || (Date.now() - (msg.ts ?? 0)) < RECENT_FINAL_MS;
    if (!turnIsLive) return;
    const matchable = heardText.slice(Math.min(consumedLenRef.current, heardText.length));
    if (!matchable) return;
    // 'out'-phase entries are deliberately NOT excluded: a fresh mention may resurrect a tag
    // mid-fade (admitTag supersedes the exit). The earned set is what stops the same sentence
    // doing that on its own.
    const exclude = new Set([
      ...entriesRef.current.filter((e) => e.phase !== 'out').map((e) => e.key),
      ...earnedRef.current,
    ]);
    const newlyMatched = suppressRedundantTags({
      candidates: matchAttributes({ text: matchable, product, chapterId, tagEligibility, exclude }),
      live: entriesRef.current.filter((e) => e.phase !== 'out').map((e) => e.key),
      contentFor: (key) => resolveTagContent(key, product),
    });
    // Which of these keys were already matchable on the PREVIOUS run (deferred for lack of a
    // slot), and which arise from words scanned for the first time? Two regex passes over a
    // sentence-sized string — micro-work, and it is what keeps the two admission rules apart.
    const scannedRel = Math.max(
      0, Math.min(scannedLenRef.current - consumedLenRef.current, matchable.length),
    );
    const carriedOver = new Set(scannedRel > 0
      ? matchAttributes({
        text: matchable.slice(0, scannedRel), product, chapterId, tagEligibility, exclude,
      })
      : []);
    scannedLenRef.current = consumedLenRef.current + matchable.length;
    if (newlyMatched.length === 0) return;
    const fresh = newlyMatched.filter((key) => !carriedOver.has(key));
    // Concurrency-aware admission (review 2026-08-26): a burst larger than the free slots WAITS
    // for slots instead of evicting. Evict-on-admit is only right for the trickle path — the
    // evicted tag has had most of its dwell; in a burst the "evicted" tag would be a sibling
    // admitted the same tick, killed at opacity 0 before ever being painted. The overflow is
    // deliberately NOT earned: it re-matches on a later run (a reveal tick, or the entries change
    // when a dwell ends) as a CARRIED-OVER key, which only ever takes a free slot — pacing a long
    // buffered answer through the frame a few at a time. A single freshly-heard match with the
    // frame full keeps the client-decided trickle behaviour: the oldest live tag eases out early.
    const aliveCount = entriesRef.current.filter((e) => e.phase !== 'out').length;
    const slots = Math.max(0, MAX_VISIBLE_TAGS - aliveCount);
    const admitted = slots > 0
      ? newlyMatched.slice(0, slots)
      : (fresh.length === 1 && newlyMatched.length === 1 ? newlyMatched : []);
    admitted.forEach((key, i) => {
      earnedRef.current.add(key);
      admitTag(key, i);
    });
    // `entries` is a dependency so a deferred over-cap key retries the moment a slot frees, even
    // when the transcript has stopped growing.
  }, [heardText, agentMessage, canShow, product, chapterId, tagEligibility, admitTag, entries,
    staticTags]);

  // Resolve content, then position. Both stages can legitimately drop a key:
  //   • no resolvable content — a matched key whose value isn't on the resolved product;
  //   • no authored anchor — the design doesn't place that key in this chapter (see
  //     chapterTagAnchors' recorded design/config mismatches). Rendering it at an invented
  //     coordinate would point at the wrong part of the watch, so it is skipped instead.
  // There is no global slot cap any more: the anchor table itself is the per-chapter cap.
  // Static chapters carry their content verbatim from the authored frames — the desktop phrase as
  // a label-less `value`, the mobile plate's `lines` — never product data. Dynamic chapters
  // resolve from the product as before.
  const withContent = staticTags
    ? entries
        .map(({ key, phase }) => {
          const def = staticTags.find((t) => t.key === key);
          return def ? { key, phase, content: def.content } : null;
        })
        .filter(Boolean)
    : product
      ? entries
          .map(({ key, phase }) => ({ key, phase, content: resolveTagContent(key, product) }))
          .filter((t) => t.content)
      : [];

  // Native-space placement is sticky (tagPlacement.js): authored plates win, region-derived tags
  // cascade once and then keep their slot for as long as they are mounted. Mutating the ref cache
  // during render is deliberate — it is an idempotent memo, not state.
  const anchorFor = resolveStickyAnchors({
    coolingSlots: coolingSlotsRef.current,
    keys: withContent.map((t) => t.key),
    // Exiting tags keep their cached slot (they are still fading at it) but may not claim a NEW
    // one — a slot spent on a tag already leaving would push a live sibling down the cascade.
    freshKeys: new Set(withContent.filter((t) => t.phase !== 'out').map((t) => t.key)),
    cache: placementCacheRef.current,
    cacheSig: placementSigRef,
    chapterId,
    breakpoint,
    tagRegions,
    isMobile,
  });
  const resolved = withContent.map((t) => ({ ...t, anchor: anchorFor(t.key) }));

  const placed = resolved.filter((t) => t.anchor);

  // Mobile places by authored left-to-right order, not by coordinate, so a tag that matched second
  // still sits where the design puts it rather than where it arrived. Desktop is unaffected: its
  // tags are absolutely positioned, so render order carries no meaning there.
  const mobileTags = isMobile
    ? orderedKeysForChapter(chapterId, 'mobile')
        .map((key) => placed.find((t) => t.key === key))
        .filter(Boolean)
    : [];

  // Recomputed per render because the plate scales with the picture while the bar it must clear is
  // viewport chrome — see maxTopPctFor.
  const maxTopPct = desktopLift
    ? Math.max(maxTopPctFor(videoBox) - desktopLiftPct(desktopLift, viewportWidth, viewportHeight), SAFE_BAND_MIN_PCT)
    : maxTopPctFor(videoBox); // SPEC-82 OQ-3: lower the ceiling by the row, never when it is closed

  const mapped = isMobile ? [] : placed
    .map(({ key, phase, content, anchor }) => {
      const point = mapNativePoint(videoBox, anchor.x, anchor.y);
      if (!point) return null; // viewport not measured yet (first paint)
      // The cover crop has taken this anchor off screen entirely (a very tall or very narrow
      // window). Drop it: clamping would park the tag at an edge tens of percent from where it
      // belongs, labelling whatever the guest can actually see there.
      if (!point.visible) return null;
      const left = clampPercent(point.leftPct, SAFE_BAND_MIN_PCT, SAFE_BAND_MAX_LEFT_PCT);
      const top = clampPercent(point.topPct, SAFE_BAND_MIN_PCT, maxTopPct);
      // The box hugs its content (PointTag: width max-content) up to this ceiling, so wrapping is
      // driven by the resolved copy, not by the anchor's authored width — that width was sized to
      // the Figma copy and wrapped any longer value mid-phrase (reported 2026-08-27). It still may
      // never reach past the right edge: a long value on a right-side anchor (crown's
      // Case_Material sits at ~70%) would otherwise run off screen.
      const roomToEdgePct = 100 - left - SAFE_BAND_MIN_PCT;
      const maxWidthPct = Math.max(Math.min(MAX_TAG_WIDTH_PCT, roomToEdgePct), MIN_TAG_WIDTH_PCT);
      // The de-clash pass below needs the plate's real footprint, and since 2026-09-04 `anchor.h`
      // is not it: the anchors were sized for the old two-part plate and overstate a single-line
      // tag by ~40%. Feeding them here is the same class of mistake as the 2026-08-28 `maxWidthPct`
      // regression on the horizontal axis — tags shoved apart to resolve an overlap that is not
      // there. Uses the same two-line reservation as the footer ceiling, so both passes agree.
      const heightPct = (
        (authoredPlateHeight('desktop', TAG_WRAP_LINES) * videoBox.scale) / videoBox.containerH
      ) * 100;
      // The plate's own footprint, for the de-clash pass below — NOT `maxWidthPct`, which is the
      // wrap CEILING (30% of the viewport) and describes no plate that exists. Feeding the ceiling
      // to the overlap test made every pair of tags in the left half of the frame read as
      // overlapping: on `exploded` it shoved the authored "3D Micro Indexes" plate a third of the
      // frame UP, above "Diamond-Cut Hands", when the design sets it below and to the right
      // (45:1968; reported 2026-08-28). The authored width is the same geometry the native-space
      // cascade already reasons about in `platesClash`, so the two passes now agree.
      const widthPct = ((anchor.w * videoBox.scale) / videoBox.containerW) * 100;
      // Only the anchors flagged `clip: true` in chapterTagAnchors.js have a neighbor close enough
      // below them that an over-long resolved value must clip instead of growing into it (e.g.
      // Movement → Power_Reserve on the movement chapter, 51px apart). Every other anchor has open
      // space below it in the authored frame, so it grows to fit its content like the Figma plates
      // themselves do — capping it unconditionally clipped ordinary, uncollided copy.
      const maxHeightPx = anchor.clip ? anchor.h * videoBox.scale : undefined;
      return { key, phase, content, left, top, heightPct, widthPct, maxWidthPct, maxHeightPx };
    })
    .filter(Boolean);

  // The native-space cascade cleared every plate in the AUTHORED frame, but the safe-band clamps
  // above run in SCREEN space and can collapse two distinct anchors onto the same band (routine on
  // wider-than-16:9 viewports, where the crop pushes several lower-half anchors into the footer
  // ceiling together). Resolve what is actually about to be painted — stickily, so a tag that
  // dodged a neighbour does not slide back when that neighbour fades out.
  const adjustedTops = stickyScreenTops({
    rects: mapped.map(({ key, left, top, widthPct, heightPct }) => ({
      key, left, top, width: widthPct, height: heightPct,
    })),
    cache: screenTopCacheRef.current,
    cacheSig: screenTopSigRef,
    sig: `${chapterId}|${breakpoint}|${videoBox ? `${videoBox.containerW}x${videoBox.containerH}` : '0'}`,
    bounds: { minTop: SAFE_BAND_MIN_PCT, maxTop: maxTopPct },
  });
  const tags = mapped.map((t, i) => ({
    ...t,
    position: { left: `${t.left}%`, top: `${adjustedTops[i]}%` },
  }));

  const droppedKeys = resolved.filter((t) => !t.anchor).map((t) => t.key);

  // A breakpoint with no authored table at all is a known state, not a per-key design gap — say so
  // once instead of naming every key on every chapter change. Both compositions are populated as of
  // Phase 4b, so this branch is now dormant; it stays as the guard for whatever composition comes
  // next, which is the case it was written for.
  const anchorsPending = !hasAnchorsForBreakpoint(breakpoint);
  const warnedRef = useRef(new Set());
  const droppedSignature = droppedKeys.join(',');
  useEffect(() => {
    if (!droppedSignature) return;
    if (anchorsPending) {
      if (warnedRef.current.has(breakpoint)) return;
      warnedRef.current.add(breakpoint);
      console.warn(
        `[PointTagLayer] the "${breakpoint}" breakpoint has no authored anchors yet (SPEC-26 `
        + 'Phase 4b) — point-tags are suppressed there rather than reusing desktop coordinates.',
      );
      return;
    }
    console.warn(
      `[PointTagLayer] no authored anchor for ${droppedSignature.split(',').join(', ')} in chapter `
      + `"${chapterId}" (${breakpoint}) — tag suppressed rather than placed at a guessed position. `
      + 'Add the coordinates to chapterTagAnchors.js or drop the key from reel_tag_eligibility.',
    );
  }, [droppedSignature, chapterId, breakpoint, anchorsPending]);

  // Rendering is NOT gated on `canShow`: entries mid-fade stay mounted so their ease-out is
  // visible over the first beats of whatever ended the hold. Admission is what `canShow` gates.
  const visible = isMobile ? mobileTags : tags;
  if ((!product && !staticTags) || visible.length === 0) return null;

  if (isMobile) {
    return (
      <div style={styles.layer}>
        <MobileTagRow
          tags={mobileTags}
          scale={mobileUnitFor(viewportWidth)}
          reducedMotion={reducedMotion}
          // `hasCard` is always false now (owner instruction 2026-09-15): ReelProductCard.js no
          // longer renders on mobile at all, so there is never a card for this row to clear —
          // lifting it would just open a dead gap under the tags where the card used to sit.
          bottom={mobileRowBottom(chapterId, false)}
        />
      </div>
    );
  }

  return (
    <div style={styles.layer}>
      {tags.map(({ key, phase, content, position, maxWidthPct, maxHeightPx }) => (
        <PointTag
          key={key}
          label={content.label}
          value={content.value}
          variant={content.variant}
          anchor={position}
          scale={videoBox?.scale ?? 1}
          maxWidthPct={maxWidthPct}
          maxHeightPx={maxHeightPx}
          opacity={phase === 'live' ? 1 : 0}
          reveal={DESKTOP_TAG_REVEAL}
          reducedMotion={reducedMotion}
        />
      ))}
    </div>
  );
}

const styles = {
  layer: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
  },
};

export default PointTagLayer;
